import React, { useEffect, useRef, useState } from 'react';
import useMitiSlotMachine from '../../hooks/useMitiSlotMachine';
import useDraggablePanel from '../../hooks/useDraggablePanel';
import SpecificSkillExpertResponseCard from '../report/SpecificSkillExpertResponseCard';
import ContemplatingIndicator from '../chat/ContemplatingIndicator';
import SkillUsedReasoningCard from '../report/SkillUsedReasoningCard';
import LectureReminderNotice from './LectureReminderNotice';
import { submitEvaluatorReport } from '../../services/api';
import {
  hasSeenLecture,
  markLectureSeen,
  subscribeToLectureViewingState,
} from '../../services/lectureViewingState';
import {
  getComplexReflectionsLectureWallpaperUrl,
  getSimpleReflectionLectureWallpaperUrl,
  getSpecificSkillsLectureWallpaperUrl,
} from '../../assets/assetPaths';

const MITI_CODE_CLASS = {
  Q: 'miti-chip--Q',
  OQ: 'miti-chip--Q',
  CQ: 'miti-chip--Q',
  S: 'miti-chip--S',
  AF: 'miti-chip--AF',
  SR: 'miti-chip--SR',
  CR: 'miti-chip--CR',
  Seek: 'miti-chip--Seek',
  GI: 'miti-chip--GI',
  Persuade: 'miti-chip--Persuade',
  Persuade_with: 'miti-chip--Persuade_with',
  Emphasize: 'miti-chip--Emphasize',
  Confront: 'miti-chip--Confront',
  NC: 'miti-chip--NC',
};

const MITI_DISPLAY_NAMES = {
  SR: 'Simple Reflection',
  CR: 'Complex Reflection',
  OQ: 'Open-Ended Question',
  CQ: 'Closed Question',
  Q: 'Question',
  S: 'Summary',
  AF: 'Affirmation',
  GI: 'Giving Information',
  Seek: 'Seeking Collaboration',
  Persuade: 'Persuade',
  Persuade_with: 'Persuade with Permission',
  Emphasize: 'Emphasize Autonomy',
  Confront: 'Confront',
  NC: 'Not Coded',
};

const OARS_SKILL_LABEL_KEYS = {
  OQ: 'chat_oars_filter_open',
  AF: 'chat_oars_filter_affirm',
  SR: 'chat_oars_filter_reflect',
  CR: 'chat_oars_filter_complex_reflect',
  S: 'chat_oars_filter_summarize',
};

function SidebarMitiChip({ label }) {
  const codeClass = MITI_CODE_CLASS[label] ?? 'miti-chip--NC';
  const display = MITI_DISPLAY_NAMES[label] ?? label;
  return <span className={`miti-chip ${codeClass}`}>{display}</span>;
}

function OarsSidebarPanel({
  theme = 'dark',
  t,
  oarsExercise = null,
  oarsExerciseLoading = false,
  oarsAttempt = null,
  oarsAnalyzing = false,
  oarsStreamingEvaluation = null,
  showConversationContext = false,
  onOarsNextExercise,
  onOarsTryAgain,
  isSpecificSkillReplay = false,
  suppressLectureReminder = false,
  onReturnToAccount,
  onShowAutomatedMiscMitiCounsellor,
  showOarsGuidedHighlight,
  showSpecificSkillsPostAttemptCue = false,
  oarsDropdownRef,
  isOarsDropdownOpen,
  setIsOarsDropdownOpen,
  oarsFilter,
  onOarsFilterChange,
  WheelArrowUp,
  WheelArrowDown,
  DiceIcon,
}) {
  const [isLectureVideoOpen, setIsLectureVideoOpen] = useState(false);
  const lectureDrag = useDraggablePanel(isLectureVideoOpen);
  const [lectureReminderOpen, setLectureReminderOpen] = useState(false);
  const [lectureViewingRevision, setLectureViewingRevision] = useState(0);
  const lectureVideoRef = useRef(null);
  const [showExpertResponse, setShowExpertResponse] = useState(false);
  const [feedbackReportOpen, setFeedbackReportOpen] = useState(false);
  const [feedbackReportText, setFeedbackReportText] = useState('');
  const [feedbackReportSubmitting, setFeedbackReportSubmitting] = useState(false);
  const [guidanceExpanded, setGuidanceExpanded] = useState(false);
  const [feedbackScrolled, setFeedbackScrolled] = useState(false);
  const feedbackScrollRef = useRef(null);
  const rawPatientDescription = String(oarsExercise?.patient_description || '').trim();
  const patientPrefix = '<b>Patient:</b>';
  const patientDescriptionText = rawPatientDescription.startsWith(patientPrefix)
    ? rawPatientDescription.slice(patientPrefix.length).trim()
    : rawPatientDescription;
  const showDrawerContext = showConversationContext && patientDescriptionText && !oarsAttempt && !oarsAnalyzing;
  const showDrawerFeedback = showConversationContext && (oarsAttempt || oarsAnalyzing);
  const visiblePredictedLabels = oarsAttempt
    ? (oarsAttempt.predicted_labels?.length ? oarsAttempt.predicted_labels : ['NC'])
    : (oarsStreamingEvaluation?.predicted_labels || []);
  const visibleGuidance = oarsAttempt?.evaluation?.feedback || oarsStreamingEvaluation?.feedback || '';
  const classifierExplanation = oarsAttempt?.classifier_explanation || oarsStreamingEvaluation?.classifier_explanation || '';
  const expertResponseText = oarsExercise?.target_turn?.text || oarsAttempt?.target_turn_text || '';
  const mitiSlotLabel = useMitiSlotMachine(
    oarsAnalyzing && !oarsAttempt && visiblePredictedLabels.length === 0,
  );
  const isComplexReflectionExercise = (
    oarsExercise?.oars_category === 'CR' ||
    oarsAttempt?.oarsCategory === 'CR' ||
    (oarsAttempt?.ground_truth_labels || []).includes('CR') ||
    String(oarsExercise?.id || oarsAttempt?.exercise_id || '').startsWith('CR_')
  );
  const feedbackReportWordCount = feedbackReportText.trim()
    ? feedbackReportText.trim().split(/\s+/).filter(Boolean).length
    : 0;
  const lectureVideo =
    oarsFilter === 'CR'
      ? {
          id: 'complex-reflections',
          embedUrl: 'https://www.youtube.com/embed/PZtp-UOSTPY',
          thumbnailUrl: getComplexReflectionsLectureWallpaperUrl(theme),
          title: 'Complex Reflections lecture video',
          modalTitle: 'Complex Reflections Lecture Video',
        }
      : oarsFilter === 'SR'
      ? {
          id: 'simple-reflections',
          embedUrl: 'https://www.youtube.com/embed/bVTEFhq5mAs',
          thumbnailUrl: getSimpleReflectionLectureWallpaperUrl(theme),
          title: 'Simple Reflections lecture video',
          modalTitle: 'Simple Reflections Lecture Video',
        }
      : {
          id: 'specific-skills',
          embedUrl: 'https://www.youtube.com/embed/NYiqMUpmm-Q',
          thumbnailUrl: getSpecificSkillsLectureWallpaperUrl(theme),
          title: 'Specific Skills lecture video',
          modalTitle: 'Specific Skills Lecture Video',
        };

  useEffect(() => subscribeToLectureViewingState(() => {
    setLectureViewingRevision((revision) => revision + 1);
  }), []);

  useEffect(() => {
    const isMainSpecificSkillsEntry = !oarsFilter && !oarsExercise?.id && !oarsExerciseLoading;
    const isLoadedSkillExercise = Boolean(oarsFilter && oarsExercise?.id);

    if (
      (!isMainSpecificSkillsEntry && !isLoadedSkillExercise) ||
      isSpecificSkillReplay ||
      suppressLectureReminder ||
      hasSeenLecture(lectureVideo.id)
    ) {
      setLectureReminderOpen(false);
      return;
    }

    setLectureReminderOpen(true);
  }, [
    lectureVideo.id,
    lectureViewingRevision,
    isSpecificSkillReplay,
    oarsExercise?.id,
    oarsExerciseLoading,
    oarsFilter,
    suppressLectureReminder,
  ]);

  const handleOpenLectureVideo = () => {
    markLectureSeen(lectureVideo.id);
    setLectureReminderOpen(false);
    setIsLectureVideoOpen(true);
  };

  useEffect(() => {
    setGuidanceExpanded(false);
    setFeedbackScrolled(false);
    if (feedbackScrollRef.current) {
      feedbackScrollRef.current.scrollTop = 0;
    }
  }, [oarsAttempt]);

  useEffect(() => {
    if (!oarsAttempt) {
      setShowExpertResponse(false);
    }
  }, [oarsAttempt, oarsExercise?.id]);

  const closeFeedbackReport = () => {
    setFeedbackReportOpen(false);
    setFeedbackReportText('');
    setFeedbackReportSubmitting(false);
  };

  const handleFeedbackReportSubmit = async () => {
    if (!oarsExercise?.id || feedbackReportSubmitting) {
      return;
    }

    setFeedbackReportSubmitting(true);
    try {
      await submitEvaluatorReport({
        attemptId: oarsAttempt?.attempt_id || oarsAttempt?.id || null,
        exerciseId: oarsAttempt?.exercise_id || oarsExercise.id,
        sessionInput: oarsAttempt?.utterance || '',
        evaluatorFeedback: oarsAttempt?.evaluation?.feedback || '',
        reportText: feedbackReportText.trim(),
      });
      closeFeedbackReport();
    } catch {
      setFeedbackReportSubmitting(false);
    }
  };

  return (
    <>
      <div className="oars-sidebar-filters">
        <div className="oars-sidebar-controls">
          <label className="selector-label">{t('sidebar_oars_label')}</label>

          <div
            className={`client-wheel-picker${showOarsGuidedHighlight ? ' client-selection-cue' : ''}`}
            ref={oarsDropdownRef}
          >
            <div className="wheel-inline-row">
              <span className="wheel-inline-label">{t('sidebar_oars_wheel_label')}</span>
              <div className="wheel-horizontal-control">
                <div className="oars-skill-dropdown">
                  <div
                    className={`wheel-window oars-dropdown-trigger${isOarsDropdownOpen ? ' open' : ''}`}
                    onClick={() => setIsOarsDropdownOpen((p) => !p)}
                    tabIndex={0}
                    role="button"
                    aria-haspopup="listbox"
                    aria-expanded={isOarsDropdownOpen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsOarsDropdownOpen((p) => !p);
                      }
                    }}
                    data-guided-highlight-purpose={showOarsGuidedHighlight ? 'Choose a Specific Skill to practise.' : undefined}
                  >
                    <span className="wheel-window-number">{oarsFilter || '--'}</span>
                    <div className="wheel-scroll-track" aria-hidden="true">
                      <div className="oars-dropdown-arrow">
                        {isOarsDropdownOpen ? <WheelArrowUp /> : <WheelArrowDown />}
                      </div>
                    </div>
                  </div>
                  {isOarsDropdownOpen && (
                    <div className="oars-skill-dropdown-menu" role="listbox">
                      {[
                        { value: 'OQ', shortLabel: 'OQ', labelKey: 'chat_oars_filter_open' },
                        { value: 'AF', shortLabel: 'AF', labelKey: 'chat_oars_filter_affirm' },
                        { value: 'SR', shortLabel: 'SR', labelKey: 'chat_oars_filter_reflect' },
                        { value: 'CR', shortLabel: 'CR', labelKey: 'chat_oars_filter_complex_reflect' },
                        { value: 'S', shortLabel: 'S', labelKey: 'chat_oars_filter_summarize' },
                      ].map(({ value, shortLabel, labelKey, disabled }) => (
                        <button
                          key={value}
                          type="button"
                          role="option"
                          aria-selected={oarsFilter === value}
                          className={`oars-skill-dropdown-option${oarsFilter === value ? ' selected' : ''}`}
                          onClick={() => {
                            if (disabled) return;
                            onOarsFilterChange && onOarsFilterChange(value);
                            setIsOarsDropdownOpen(false);
                          }}
                        >
                          <span className="oars-skill-dropdown-option-letter">{shortLabel || value}</span>
                          {t(labelKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="random-client-row">
              <span className="wheel-inline-label">{t('sidebar_oars_random_label')}</span>
              <button
                className="random-client-btn"
                onClick={() => onOarsFilterChange && onOarsFilterChange(null)}
                aria-label={t('sidebar_oars_random_select')}
                title={t('sidebar_oars_random_select')}
                data-guided-highlight-purpose={showOarsGuidedHighlight ? 'Start with a randomly selected reflection exercise.' : undefined}
              >
                <DiceIcon />
              </button>
            </div>
          </div>
        </div>

        {lectureVideo && (
          <div className="sidebar-lecture-video">
            <p className="selector-description sidebar-lecture-video-label">Accompanying Lecture Video:</p>
            <button
              type="button"
              ref={lectureVideoRef}
              className="sidebar-lecture-video-frame"
              onClick={handleOpenLectureVideo}
              aria-label={`Open ${lectureVideo.title}`}
              title={`Open ${lectureVideo.title}`}
            >
              <img
                src={lectureVideo.thumbnailUrl}
                alt={`${lectureVideo.title} thumbnail`}
              />
            </button>
          </div>
        )}

        {showDrawerContext && (
          <div className="oars-context-panel oars-sidebar-context-panel">
            <div className="analytics-header">
              <h4 className="analytics-title">Conversation Context</h4>
              <p className="analytics-subtitle">Background on the client for this exercise</p>
            </div>
            <div className="oars-context-body">
              <div>{patientDescriptionText}</div>
            </div>
            {onOarsNextExercise && (
              <div className="oars-action-bar">
                <div className="oars-next-exercise-wrap">
                  <button
                    type="button"
                    className="oars-btn oars-btn--primary"
                    onClick={onOarsNextExercise}
                  >
                    {t('report_oars_next_exercise')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showDrawerFeedback && (
          <div
            className={`oars-sidebar-feedback-divider${feedbackScrolled ? ' oars-sidebar-feedback-divider--visible' : ''}`}
            aria-hidden="true"
          />
        )}

        {showDrawerFeedback && (
          <div
            ref={feedbackScrollRef}
            className={`oars-sidebar-feedback-scroll${feedbackScrolled ? ' oars-sidebar-feedback-scroll--scrolled' : ''}`}
            onScroll={(event) => setFeedbackScrolled(event.currentTarget.scrollTop > 1)}
          >
            <div className="analytics-sections">
              <SkillUsedReasoningCard
                explanation={classifierExplanation}
                codeCount={visiblePredictedLabels.length}
                theme={theme}
              >
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Skill Used</h5>
                  {onShowAutomatedMiscMitiCounsellor && (
                    <button
                      type="button"
                      className="analytics-toggle-icon"
                      onClick={onShowAutomatedMiscMitiCounsellor}
                      aria-label="Open counsellor coding manual"
                      title="Open counsellor coding manual"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z" />
                        <path d="M6.5 3v13" />
                        <path d="M8.5 7h8" />
                        <path d="M8.5 10.5h8" />
                        <path d="M8.5 14h5" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="analytics-card-content">
                  {oarsAttempt || visiblePredictedLabels.length > 0 ? (
                    <div className="oars-attempt-entry">
                      <div className="oars-label-row">
                        {visiblePredictedLabels.map((label) => (
                          <SidebarMitiChip key={label} label={label} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="oars-attempt-entry miti-slot-machine" aria-label="Analyzing skill code">
                      <div className="oars-label-row" aria-hidden="true">
                        <SidebarMitiChip key={mitiSlotLabel} label={mitiSlotLabel} />
                      </div>
                    </div>
                  )}
                </div>
              </SkillUsedReasoningCard>

              <div className="analytics-card">
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Guidance</h5>
                  <div className="oars-guidance-actions">
                    {oarsAttempt?.evaluation && (
                      <button
                        type="button"
                        className={`analytics-toggle-icon oars-guidance-expand-btn${!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? ' oars-guidance-expand-btn--selection-cue' : ''}`}
                        data-guided-highlight-purpose={!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? 'Open your Guidance in a larger view.' : undefined}
                        onClick={() => setGuidanceExpanded(true)}
                        aria-label="Open guidance in larger view"
                        title="Open guidance in larger view"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                          <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                          <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                          <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                        </svg>
                      </button>
                    )}
                    <button
                      type="button"
                      className="analytics-toggle-icon feedback-report-icon"
                      onClick={() => setFeedbackReportOpen(true)}
                      aria-label="Report incorrect feedback"
                      title={oarsAttempt?.evaluation ? 'Report incorrect feedback' : 'Available when guidance is complete'}
                      disabled={!oarsAttempt?.evaluation}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 21V5" />
                        <path d="M4 5h10l-1.4 3L18 11H4" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="analytics-card-content">
                  {visibleGuidance
                    ? <p className={`sr-feedback-text${oarsAnalyzing ? ' specific-skill-guidance-streaming' : ''}`}>{visibleGuidance}</p>
                    : <p className="analytics-empty"><ContemplatingIndicator /></p>
                  }
                </div>
              </div>

              <SpecificSkillExpertResponseCard
                t={t}
                responseText={expertResponseText}
                revealed={showExpertResponse}
                onToggle={() => setShowExpertResponse((value) => !value)}
                showComplexReflectionHint={isComplexReflectionExercise}
              />
              <div className="oars-sidebar-feedback-tail" aria-hidden="true" />
            </div>
          </div>
        )}

        {showDrawerFeedback && oarsAttempt && (
          <div className="oars-sidebar-action-dock">
            <div className="oars-action-bar">
              <button
                type="button"
                className={`oars-btn oars-btn--secondary${!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? ' oars-btn--selection-cue' : ''}`}
                data-guided-highlight-purpose={!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? 'Try another response to this same exercise.' : undefined}
                onClick={onOarsTryAgain}
              >
                {t('report_oars_try_again')}
              </button>
              <div className="oars-next-exercise-wrap">
                <button
                  type="button"
                  className={`oars-btn oars-btn--primary${showSpecificSkillsPostAttemptCue ? ' oars-btn--selection-cue' : ''}`}
                  data-guided-highlight-purpose={showSpecificSkillsPostAttemptCue ? 'Continue to a new Specific Skills exercise.' : undefined}
                  onClick={isSpecificSkillReplay ? onReturnToAccount : onOarsNextExercise}
                >
                  {isSpecificSkillReplay ? t('account_return_to_account') : t('report_oars_next_exercise')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {feedbackReportOpen && (
        <div className="app-confirm-overlay app-feedback-overlay" role="dialog" aria-modal="true">
          <div className="app-confirm-panel app-feedback-panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="app-feedback-close"
              onClick={closeFeedbackReport}
              aria-label="Close report dialog"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
            <div className="app-dialog-head">
              <h4 className="app-dialog-title">Think Our Evaluator is Incorrect?</h4>
            </div>
            <div className="app-confirm-content">
              <p>If something in this evaluation seems off, you can flag it here so we can review it more closely.</p>
            </div>
            <div className="app-feedback-field">
              <div className="app-feedback-field-header">
                <label className="app-feedback-label" htmlFor="sidebar-report-feedback-input">Your Feedback</label>
                <span className="app-feedback-word-count">{`${feedbackReportWordCount} / 150 words`}</span>
              </div>
              <textarea
                id="sidebar-report-feedback-input"
                className="app-feedback-input"
                value={feedbackReportText}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const nextCount = nextValue.trim()
                    ? nextValue.trim().split(/\s+/).filter(Boolean).length
                    : 0;
                  if (nextCount <= 150) {
                    setFeedbackReportText(nextValue);
                  }
                }}
                rows={4}
                placeholder="Share your thoughts here..."
              />
            </div>
            <div className="app-dialog-actions app-feedback-actions">
              <button
                type="button"
                className="app-confirm-btn primary"
                onClick={handleFeedbackReportSubmit}
                disabled={feedbackReportSubmitting}
              >
                {feedbackReportSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      {guidanceExpanded && (
        <div className="oars-info-overlay" onClick={() => setGuidanceExpanded(false)}>
          <div
            className="oars-info-modal oars-guidance-expanded-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oars-guidance-expanded-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="oars-guidance-expanded-header">
              <h3 id="oars-guidance-expanded-title">Guidance</h3>
              <button
                type="button"
                className="oars-info-modal__close oars-guidance-expanded-close"
                onClick={() => setGuidanceExpanded(false)}
                aria-label="Close guidance"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="oars-info-modal__body oars-guidance-expanded-body">
              {oarsAttempt?.evaluation?.feedback ? (
                <p>{oarsAttempt.evaluation.feedback}</p>
              ) : (
                <p>Analyzing...</p>
              )}
            </div>
          </div>
        </div>
      )}
      {isLectureVideoOpen && lectureVideo && (
        <div
          className="sidebar-lecture-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sidebar-lecture-video-title"
          onClick={() => setIsLectureVideoOpen(false)}
        >
          <div
            className="sidebar-lecture-modal-panel"
            ref={lectureDrag.panelRef}
            style={lectureDrag.panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`sidebar-lecture-modal-header${lectureDrag.isDragging ? ' is-dragging' : ''}`}
              {...lectureDrag.dragHandleProps}
            >
              <h3 id="sidebar-lecture-video-title">{lectureVideo.modalTitle}</h3>
              <button
                type="button"
                className="sidebar-lecture-modal-close"
                onClick={() => setIsLectureVideoOpen(false)}
                aria-label="Close lecture video"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="sidebar-lecture-modal-video">
              <iframe
                src={lectureVideo.embedUrl}
                title={lectureVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
      {lectureReminderOpen && (
        <LectureReminderNotice
          anchorRef={lectureVideoRef}
          t={t}
          theme={theme}
          skillName={OARS_SKILL_LABEL_KEYS[oarsFilter]
            ? t(OARS_SKILL_LABEL_KEYS[oarsFilter])
            : (MITI_DISPLAY_NAMES[oarsFilter] || oarsFilter || t('mode_oars'))}
          onClose={() => setLectureReminderOpen(false)}
        />
      )}
    </>
  );
}

export default OarsSidebarPanel;
